import React, {useEffect, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {useTranslation} from 'react-i18next'
import OnboardingApi from '../../Api/onboarding'

type Props = {
  onSuccess: () => void
  onSkip: () => void
}

export default function OnboardingStepRefund({onSuccess, onSkip}: Props) {
  const {t} = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [mode, setMode] = useState<'enable' | 'skip'>('skip')
  const [duration, setDuration] = useState('30')

  useEffect(() => {
    OnboardingApi.getRefund((res: any) => {
      const data = res?.data || {}
      if (Number(data.is_refund_order) === 1) {
        setMode('enable')
        setDuration(String(data.refund_duration || 30))
      }
    }, setIsLoading)
  }, [])

  const save = (is_refund_order: boolean) => {
    OnboardingApi.saveRefund(
      {
        is_cancel_order: true,
        is_refund_order,
        refund_duration: is_refund_order ? Number(duration) || 30 : null,
        refund_duration_type: is_refund_order ? 'day' : null,
      },
      () => (is_refund_order ? onSuccess() : onSkip()),
      setIsSaving
    )
  }

  if (isLoading) {
    return (
      <Stack alignItems='center' py={6}>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack gap={2}>
      <Typography fontWeight={600} fontSize={18}>
        {t('onboarding.step3_title') as string}
      </Typography>
      <Alert icon={<InfoOutlinedIcon fontSize='inherit' />} severity='info'>
        {t('onboarding.step3_optional_hint') as string}
      </Alert>

      <RadioGroup
        value={mode}
        onChange={(e) => setMode(e.target.value as 'enable' | 'skip')}
      >
        <FormControlLabel
          value='enable'
          control={<Radio />}
          label={t('onboarding.refund_enable') as string}
        />
        <FormControlLabel
          value='skip'
          control={<Radio />}
          label={t('onboarding.refund_skip') as string}
        />
      </RadioGroup>

      {mode === 'enable' ? (
        <TextField
          label={t('RefundDuration') as string}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          type='number'
          fullWidth
          helperText={t('onboarding.refund_days_hint') as string}
        />
      ) : (
        <Typography fontSize={13} color='text.secondary'>
          {t('onboarding.refund_skip_message') as string}
        </Typography>
      )}

      <Box display='flex' justifyContent='flex-end' gap={1}>
        <Button
          variant='outlined'
          disabled={isSaving}
          onClick={() => save(false)}
        >
          {t('onboarding.skip') as string}
        </Button>
        <Button
          variant='contained'
          disabled={isSaving}
          onClick={() => save(mode === 'enable')}
        >
          {isSaving ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            (t('onboarding.continue') as string)
          )}
        </Button>
      </Box>
    </Stack>
  )
}
